// src/components/Road.tsx

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { useCursor } from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';
import type { RoadForRender } from '../hooks/useCityData';

// ----------------------------------------------------------------------
// Props del componente
// ----------------------------------------------------------------------

export interface RoadProps {
  /** Datos de la carretera con puntos en coordenadas locales */
  road: RoadForRender;
  /** Callback cuando se selecciona la carretera */
  onSelect?: (road: RoadForRender, mesh: THREE.Mesh) => void;
  /** Si la carretera está seleccionada actualmente */
  selected?: boolean;
  /** Color opcional (sobrescribe el color por tipo) */
  color?: string;
  /** Opacidad (por defecto 1) */
  opacity?: number;
  /** Si se debe mostrar contorno al seleccionar */
  showOutline?: boolean;
}

// ----------------------------------------------------------------------
// Colores por tipo de carretera
// ----------------------------------------------------------------------

const ROAD_COLORS: Record<string, string> = {
  motorway: '#4a4a4a',
  trunk: '#505050',
  primary: '#555555',
  secondary: '#5a5a5a',
  tertiary: '#606060',
  residential: '#666666',
  service: '#6a6a6a',
  living_street: '#6e6e6e',
  footway: '#8B7355',
  path: '#8B7355',
  default: '#555555',
};

function getColorByType(highwayType: string): string {
  return ROAD_COLORS[highwayType] || ROAD_COLORS.default;
}

// ----------------------------------------------------------------------
// Materiales compartidos (una instancia por color para evitar cientos
// de materiales en GPU). Solo se usan para opacidad = 1.
// ----------------------------------------------------------------------

const materialCache = new Map<string, THREE.MeshStandardMaterial>();

function getSharedRoadMaterial(color: string): THREE.MeshStandardMaterial {
  let mat = materialCache.get(color);
  if (!mat) {
    mat = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.6,
      metalness: 0.05,
    });
    materialCache.set(color, mat);
  }
  return mat;
}

// ----------------------------------------------------------------------
// Componente principal
// ----------------------------------------------------------------------

const Road: React.FC<RoadProps> = ({
  road,
  onSelect,
  selected = false,
  color,
  opacity = 1,
  showOutline = true,
}) => {
  const [hovered, setHovered] = useState(false);
  useCursor(hovered);

  // Crear geometría de cinta a partir de los puntos
  const geometry = useMemo(() => {
    if (!road.points || road.points.length < 2) return null;

    const points = road.points;
    const halfWidth = (road.width || 8) / 2;

    const vertices: number[] = [];
    const indices: number[] = [];
    const normals: number[] = [];
    const uvs: number[] = [];

    for (let i = 0; i < points.length; i++) {
      const curr = points[i];
      const next = points[i + 1];
      const prev = points[i - 1];

      // Dirección suavizada para calcular el perpendicular
      let dir = new THREE.Vector3();
      if (prev && next) {
        const v1 = new THREE.Vector3(next.x - curr.x, 0, next.z - curr.z).normalize();
        const v2 = new THREE.Vector3(curr.x - prev.x, 0, curr.z - prev.z).normalize();
        dir = v1.add(v2).normalize();
      } else if (next) {
        dir = new THREE.Vector3(next.x - curr.x, 0, next.z - curr.z).normalize();
      } else if (prev) {
        dir = new THREE.Vector3(curr.x - prev.x, 0, curr.z - prev.z).normalize();
      } else {
        dir = new THREE.Vector3(1, 0, 0);
      }

      // Perpendicular en el plano XZ
      const perp = new THREE.Vector3(-dir.z, 0, dir.x).normalize();

      const leftX = curr.x + perp.x * halfWidth;
      const leftZ = curr.z + perp.z * halfWidth;
      const rightX = curr.x - perp.x * halfWidth;
      const rightZ = curr.z - perp.z * halfWidth;

      // Dos vértices por punto (izquierdo y derecho)
      vertices.push(leftX, 0.05, leftZ);   // izquierdo
      vertices.push(rightX, 0.05, rightZ); // derecho
      normals.push(0, 1, 0, 0, 1, 0);
      uvs.push(0, i / points.length, 1, i / points.length);
    }

    // Índices para formar triángulos entre segmentos
    for (let i = 0; i < points.length - 1; i++) {
      const a = i * 2;
      const b = i * 2 + 1;
      const c = (i + 1) * 2;
      const d = (i + 1) * 2 + 1;
      indices.push(a, c, b, c, d, b);
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geom.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    geom.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geom.setIndex(indices);
    geom.computeVertexNormals();

    return geom;
  }, [road]);

  // Material (compartido por color; si la opacidad varia, se crea uno propio)
  const material = useMemo(() => {
    const baseColor = color || getColorByType(road.highwayType);
    if (opacity < 1) {
      return new THREE.MeshStandardMaterial({
        color: baseColor,
        roughness: 0.6,
        metalness: 0.05,
        transparent: true,
        opacity,
      });
    }
    return getSharedRoadMaterial(baseColor);
  }, [road.highwayType, color, opacity]);

  // Liberar geometría y material propio al desmontar o regenerarse
  useEffect(() => {
    return () => {
      if (geometry) geometry.dispose();
      if (opacity < 1) material.dispose();
    };
  }, [geometry, material, opacity]);

  // Manejador de click
  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      event.stopPropagation();
      if (onSelect) {
        onSelect(road, event.object as THREE.Mesh);
      }
    },
    [road, onSelect]
  );

  // Si no hay geometría válida, no renderizar
  if (!geometry) return null;

  return (
    <group>
      {/* Mesh principal de la carretera */}
      <mesh
        geometry={geometry}
        material={material}
        position={[0, 0, 0]}
        onClick={handleClick}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
        }}
        onPointerOut={(e) => {
          e.stopPropagation();
          setHovered(false);
        }}
        receiveShadow
      />

      {/* Contorno de selección */}
      {selected && showOutline && (
        <lineSegments>
          <edgesGeometry args={[geometry]} />
          <lineBasicMaterial color="#ff9900" linewidth={2} />
        </lineSegments>
      )}

      {/* Indicador de hover (resaltado) */}
      {hovered && !selected && (
        <mesh geometry={geometry} position={[0, 0.02, 0]}>
          <meshBasicMaterial
            color="#ffffff"
            transparent
            opacity={0.3}
            depthWrite={false}
          />
        </mesh>
      )}
    </group>
  );
};

export default Road;